import React from 'react';
import { useStyles } from '@archipro-design/aria';
import * as S from './OptionPicker.mobile.style';
import type Variant from '../../type/variant';
import type AttributeOption from '../../type/attribute-option';
import OptionHeader from './OptionHeader.mobile';
import { useVariantOptionState } from '../../context/VariantOptionContext';
import { useVariantDecision } from '../../hook/use-variant-decision';
import OptionRadioGroup from './OptionRadioGroup.mobile';

export interface BasePickerProps {
    data: {
        variants: Variant[];
    };
    isGeneric?: boolean;
    hasManyOptions?: boolean;
}

type OptionPickerProps = BasePickerProps & {
    data: {
        attributeID: number;
        label: string;
        options: AttributeOption[];
    };
};

const emptyOption = {
    ID: 0,
    label: 'Not Applicable',
};

const OptionPicker = ({
    data: { attributeID, label, variants, options },
    isGeneric,
    hasManyOptions = true,
}: OptionPickerProps) => {
    const { decision } = useVariantOptionState();
    const { pickOption } = useVariantDecision(variants);
    const [expanded, setExpanded] = React.useState(false);
    const { css } = useStyles({ expanded });

    const selectedOptionID = decision.options[attributeID] ?? 0;

    const items = options.map((opt) => ({ ID: opt.ID, label: opt.Label }));
    const option =
        items.find((opt) => opt.ID === selectedOptionID) ?? emptyOption;

    if (!isGeneric) {
        items.unshift(emptyOption);
    }

    const toggleExpanded = () => {
        if (hasManyOptions) {
            setExpanded((e) => !e);
        }
    };
    return (
        <div className={css(S.OptionPicker)}>
            <OptionHeader
                title={label}
                subTitle={<div className={css(S.SubTitle)}>{option.label}</div>}
                expanded={expanded}
                onClick={toggleExpanded}
                hasManyOptions={hasManyOptions}
            />
            <OptionRadioGroup
                onSelect={(optionID) =>
                    pickOption({
                        attributeID,
                        ID: optionID,
                    })
                }
                visible={expanded}
                selectedID={selectedOptionID}
                items={items}
            />
        </div>
    );
};

export default OptionPicker;
